import React, { useState, useEffect } from 'react';
import {
    Typography,
    Tooltip,
    Box,
    Paper,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
} from '@mui/material';
import { GroundStationManager, SwitchManager } from 'models';

import { useAppDispatch, useAppSelector } from 'hooks';
import {
    sendGroundStationCommand,
    getGroundStationList,
} from 'state/groundstations';
import { receiveMessage } from 'state/websockets';
import { onSwitchValueUpdated } from 'state/switches';

interface Props {
    obj: SwitchManager.Switch;
    disabled?: boolean;
}

const Switch: React.FunctionComponent<Props> = ({ obj, disabled }) => {
    const dispatch = useAppDispatch();

    const { name, description, readOnly, valueToTextMapping, value } = obj;

    const possibleValues = Object.keys(valueToTextMapping);

    const { latestCommands, list } = useAppSelector(
        (root) => root.groundstations,
    );
    const { tokens } = useAppSelector((root) => root.auth);

    const [sentCommand, setSentCommand] =
        useState<GroundStationManager.API.Command>();

    const [sending, setSending] = useState(false);

    const groundstation = list.object?.find((gs) => gs.id == obj.groundStation);

    useEffect(() => {
        const latestSentCommand =
            sentCommand != null ? latestCommands[sentCommand.id] : null;

        if (latestSentCommand && groundstation) {
            if (latestSentCommand.status === 'FINISHED') {
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            latestSentCommand.results?.message?.toString() ??
                            'No Message from Ground Station',
                        status: 'info',
                    }),
                );
                const newValue = `${latestSentCommand.results?.value}`;
                if (newValue) {
                    dispatch(
                        onSwitchValueUpdated({
                            command: 'update_switch',
                            ground_station_id: groundstation.id,
                            switch_id: obj.switchId,
                            value: newValue,
                            pretty_value:
                                obj.valueToTextMapping[newValue] ?? newValue,
                        }),
                    );
                }

                setSentCommand(undefined);
            } else if (latestSentCommand.status === 'ERROR') {
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            latestSentCommand.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );
            }
        }
    }, [latestCommands]);

    useEffect(() => {
        if (list.status === 'idle' && tokens.status === 'finished') {
            dispatch(getGroundStationList());
        }
    }, [list, tokens]);

    const updateSwitch = async (newValue: string) => {
        if (!groundstation) return;

        setSending(true);

        try {
            const results = await dispatch(
                sendGroundStationCommand({
                    id: groundstation.id,
                    command: 'update_switch',
                    params: {
                        switch_id: obj.switchId,
                        new_value: newValue,
                    },
                }),
            );

            if (results.error)
                dispatch(
                    receiveMessage({
                        command: 'message',
                        is_ground_station: true,
                        from_id: groundstation.id,
                        from: groundstation.name,
                        message:
                            results.error ??
                            'No Error Message from Ground Station',
                        status: 'error',
                    }),
                );

            if (results.command) setSentCommand(results.command);
        } finally {
            setSending(false);
        }
    };

    return (
        <Paper sx={{ display: 'grid', gap: 0, p: 1 }}>
            <Tooltip
                title={
                    groundstation?.connectionId
                        ? "If the Swich will change when the Ground Station confirms it's value. It may flick back to it's original value if it could not switch"
                        : "The Ground Station isn't Connected to the Server. Cannot update Switches."
                }
                placement="top"
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        gap: 1,
                    }}
                >
                    <Typography variant="h6">{name}</Typography>

                    {sending && <CircularProgress size={24} />}
                </Box>
            </Tooltip>
            <Typography variant="subtitle2">{description}</Typography>

            <ToggleButtonGroup
                exclusive
                fullWidth
                disabled={disabled || !groundstation?.connectionId || readOnly}
                value={value}
                onChange={(_, newValue) => {
                    if (value == null || value === newValue || readOnly) return;

                    updateSwitch(newValue);
                }}
            >
                {possibleValues.map((potentialValue) => (
                    <ToggleButton value={potentialValue} key={potentialValue}>
                        {valueToTextMapping[potentialValue]}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Paper>
    );
};

Switch.displayName = 'Switch';
export default Switch;
